import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { WebChatIntlWrapper as WebChat, WebChatFunctions } from "@42maru/webchat";
import { Scene } from '@/model';
import { API_HOST, SOCKET_HOST, WEBCHAT_SOCKET_PATH } from '@/core/variables';
import { omitEmpty } from '@/util/typeUtil';
import { replaceBlobUrlToFilenameAndGetNamedFiles, refineSameNameFiles } from '@/util/fileUtil';
import { mergeThemeWithDefault } from '@/util/helper/webchatThemeHelper';
import { isValidParagraphData } from '@/util/helper/paragraphHelper';
import scenarioRepository from '@/repository/scenarioRepository';
import { useScenario, useUpdateScenario } from '@/query/scenario';
import {
    useModal, useDialog, useScenarioId,
    useScenarioAutocompletes, useConnectedUsers, useUploadedFiles, useSyncForm
} from '@/hook';
import { useCreateScene, useDeleteScene, useScenes, useUpdateScenes } from '@/query/scene';
import Tooltip from '@/component/Tooltip';
import Button from '@/component/Button';
import { errorToast, successToast } from '@/component/Toast';
import { ParagraphEditor } from '@/component/paragraphEditor';
import { RoutingBlockDialog } from '@/component/modal';
import { Textarea, NumberInput, SearchBar, SearchBarRef, TextInput } from '@/component/input';
import { UploadIcon, DownloadIcon, AddIcon, LightningBoltIcon } from '@/assets/svgs/icons';

import SceneList from './component/SceneList';
import SceneCreateModal, { SCENE_CREATE_MODAL_ID } from './component/SceneCreateModal';
import UploadScenarioModal, { SCENARIO_UPLOAD_MODAL_ID } from './component/UploadScenarioModal';


const ScenarioEditView: React.FC = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();
    const modal = useModal();
    const dialog = useDialog();
    const { isOtherConnected } = useConnectedUsers();
    const { setUserVariables, clearUserVariables } = useScenarioAutocompletes();
    const { files, clear: clearFiles } = useUploadedFiles();
    const { syncForm } = useSyncForm();

    const [showWebchat, setShowWebchat] = useState<boolean>(false);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [selectedScene, setSelectedScene] = useState<Scene>();
    const [modifiedScenes, setModifiedScenes] = useState<{ [key: number]: Scene }>({});
    const webchatFunctionsRef = useRef<WebChatFunctions>();
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const searchBarRef = useRef<SearchBarRef>(null);
    const sceneToSelectRef = useRef<Scene>();

    const { data: scenario } = useScenario(scenarioId);
    const { data: allScenes } = useScenes(scenarioId);
    const { data: searchedScenes, refetch: refetchScenes } = useScenes(scenarioId, searchKeyword);
    const { mutateAsync: createScene, isPending: isDuplicating } = useCreateScene(scenarioId);
    const { mutateAsync: updateScenes, isPending: isUpdating } = useUpdateScenes(scenarioId);
    const { mutateAsync: deleteScene, isPending: isDeleting } = useDeleteScene(scenarioId);
    const { mutateAsync: updateScenario, isPending: isUpdatingWelcomeOrFallback } = useUpdateScenario(scenarioId);

    const checkCustomIdUsed = useCallback((customId: string, id?: number) => {
        if (!customId || !allScenes?.length) {
            return false;
        }
        return allScenes.some(scene => (modifiedScenes[scene.id] ?? scene)?.custom_id?.trim() === customId.trim() && scene.id !== id);
    }, [allScenes, modifiedScenes]);
    const checkInvalidScene = useCallback((scene: Scene): boolean => {
        if (!scene.name) {
            return true;
        }
        if (!scene.custom_id || checkCustomIdUsed(scene.custom_id, scene.id)) {
            return true;
        }
        if (!scene.paragraphs?.length || !scene.paragraphs.every(isValidParagraphData)) {
            return true;
        }
        return false;
    }, [checkCustomIdUsed]);

    const isSubmitting = isUpdating || isDeleting || isDuplicating || isUpdatingWelcomeOrFallback;
    const invalidSceneIds = useMemo(() => {
        return Object.values(modifiedScenes).filter(checkInvalidScene).map(scene => scene.id);
    }, [modifiedScenes, checkInvalidScene]);

    const getModifiedScene = (scene?: Scene) => {
        if (!scene) {
            return;
        }
        return modifiedScenes[scene.id] ?? scene;
    };

    const submit = async () => {
        const refinedFiles = await refineSameNameFiles(files);
        const {
            data: scenesToSubmit,
            files: filesToSubmit
        } = replaceBlobUrlToFilenameAndGetNamedFiles(structuredClone(Object.values(modifiedScenes)), refinedFiles);

        const formData = new FormData();
        Array.from(filesToSubmit).forEach(file => {
            formData.append("files", file);
        });

        try {
            const ommitedScenes = scenesToSubmit.map((scene: Scene) => ({ ...scene, paragraphs: omitEmpty(scene.paragraphs) ?? [] }));
            formData.append("scenes", JSON.stringify(ommitedScenes));
        } catch (e) {
            console.error('Error getting form data from scenes', e);
            throw e;
        }
        return updateScenes(formData)
            .then(scenes => {
                setSelectedScene(prev => structuredClone(scenes.find(scene => scene.id === prev?.id) ?? prev));
                setModifiedScenes({});
                clearFiles();
                successToast(intl.formatMessage({ id: "t003" }));
                setTimeout(() => {
                    syncForm();
                }, 100);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };
    
    const onClickSave = async () => {
        if (Object.keys(modifiedScenes).length === 0) {
            return;
        }
        if (invalidSceneIds.length > 0) {
            return;
        }

        if (isOtherConnected) {
            dialog.open({
                variant: "primary",
                title: intl.formatMessage({ id: "i00033" }),
                content: intl.formatMessage({ id: "i00021" }),
                onConfirm: submit
            });
        } else {
            await submit();
        }
    };

    const debouncedChangeScene = useMemo(() => {
        const updateUserVariables = (scenes: Scene[], modifiedScenes: { [key: number]: Scene }) => {
            const updatedScenes = [...(scenes?.map(scene => modifiedScenes[scene.id] ?? scene) ?? [])];
            setUserVariables("scene", updatedScenes);
        };

        return _.debounce((id: number, key: keyof Scene, value: any) => {
            setModifiedScenes(prev => {
                const prevScene = prev[id] ?? structuredClone(allScenes!.find(scene => scene.id === id));
                const newModifiedScenes = {
                    ...prev,
                    [id]: {
                        ...prevScene,
                        [key]: value
                    }
                };
                updateUserVariables(allScenes!, newModifiedScenes);
                return newModifiedScenes;
            });
        }, 300);
    }, [allScenes, setUserVariables]);

    const onCreate = (scene: Scene) => {
        sceneToSelectRef.current = scene;
        searchBarRef.current?.clear();
    };

    const onClickDownload = async () => {
        try {
            const fileName = `${scenario?.name || "scenario"}.xlsx`;
            const blob = await scenarioRepository.export(scenarioId);
            const url = window.URL.createObjectURL(
                new Blob([blob], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (error) {
            console.error(error);
        }
    };

    const onChangeWelcome = async (id: number) => {
        const form = {
            ...scenario,
            properties: {
                ...scenario?.properties,
                welcome_scene_id: id
            }
        };
        await updateScenario(form)
                .then(() => {
                    sceneToSelectRef.current = allScenes?.find(scene => scene.id === id);
                    refetchScenes();
                    successToast(intl.formatMessage({ id: "t003" }));
                }).catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
    };

    const onChangeFallback = async (id: number) => {
        const form = {
            ...scenario,
            properties: {
                ...scenario?.properties,
                fallback_scene_id: id
            }
        };
        await updateScenario(form)
                .then(() => {
                    sceneToSelectRef.current = allScenes?.find(scene => scene.id === id);
                    refetchScenes();
                    successToast(intl.formatMessage({ id: "t003" }));
                }).catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
    };

    const onDuplicate = async (sceneId: number) => {
        const scene = modifiedScenes[sceneId] ?? allScenes?.find(scene => scene.id === sceneId);
        if (!scene) {
            return;
        }
        const form = {
            ...scene,
            custom_id: `${scene.custom_id}_copy`,
            name: `${scene.name} copy`,
            paragraphs: scene.paragraphs?.map(paragraph => omitEmpty(structuredClone(paragraph)) ?? {}) ?? []
        };
        await createScene(form)
                .then(newScene => {
                    sceneToSelectRef.current = newScene;
                    successToast(intl.formatMessage({ id: "t001" }));
                }).catch(error => {
                    console.error(error);
                    errorToast(intl.formatMessage({ id: "i02028" }));
                });
    };

    const onClickDelete = async (id: number) => {
        dialog.open({
            title: intl.formatMessage({ id: "i02012" }),
            content: intl.formatMessage({ id: "i02013" }),
            variant: "danger",
            onConfirm: async () => {
                let nextScene: Scene | undefined;
                if (id === selectedScene?.id && searchedScenes && searchedScenes.length > 1) {
                    const index = searchedScenes.findIndex(scene => scene.id === id);
                    if (index !== -1) {
                        nextScene = index < searchedScenes.length - 1 ? searchedScenes[index + 1] : searchedScenes[index - 1];
                    }
                }
                if (nextScene) {
                    sceneToSelectRef.current = getModifiedScene(nextScene);
                }
                await deleteScene(id)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    useEffect(() => {
        if (!searchedScenes) {
            return;
        }
        if (sceneToSelectRef.current && searchedScenes.find(scene => scene.id === sceneToSelectRef.current!.id)) {
            setSelectedScene(sceneToSelectRef.current);
            sceneToSelectRef.current = undefined;
            return;
        }
        if (selectedScene && searchedScenes.find(scene => scene.id === selectedScene.id)) {
            return;
        }
        setSelectedScene(getModifiedScene(searchedScenes?.[0]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchedScenes]);

    useEffect(() => {
        return () => {
            clearFiles();
            clearUserVariables();
        };
    }, [clearFiles, clearUserVariables]);

    useEffect(() => {
        if (showWebchat) {
            webchatFunctionsRef.current?.reload();
        }
    }, [showWebchat]);

    if (!scenario) {
        return null;
    }
    return (<>
        <SceneCreateModal checkCustomIdUsedByOthers={checkCustomIdUsed}
                          onCreateScene={onCreate}
        />
        <UploadScenarioModal />
        <RoutingBlockDialog isBlocking={Object.values(modifiedScenes).length > 0}
                            title={intl.formatMessage({ id: "i04041" })}
                            description={intl.formatMessage({ id: "i04042" })}
        />

        <main className="scenario-content-layout pr-3 flex flex-col"
        >
            <div className="search-bar-container">
                <SearchBar ref={searchBarRef}
                           width={680}
                           placeholder={intl.formatMessage({ id: "i02003" })}
                           onSearch={setSearchKeyword}
                />
            </div>

            <div className="subtitle-container min-w-md-page pr-3 mt-5">
                <Button variant="text"
                        className="py-0 text-[13px] leading-none"
                        onClick={() => modal.open(SCENE_CREATE_MODAL_ID)}
                >
                    <AddIcon className="size-3"/>
                    {intl.formatMessage({ id: "i02001" })}
                </Button>

                <div className="subtitle-container-btn-group">
                    <Button variant="text"
                            onClick={() => modal.open(SCENARIO_UPLOAD_MODAL_ID)}
                    >
                        <UploadIcon className="size-3.5"/>
                        {intl.formatMessage({ id: "b0004" })}
                    </Button>
                    <Button variant="text"
                            onClick={onClickDownload}
                    >
                        <DownloadIcon className="size-3.5"/>
                        {intl.formatMessage({ id: "b0005" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => setShowWebchat(prev => !prev)}
                    >
                        <LightningBoltIcon className="size-3" />
                        {intl.formatMessage({ id: "i02002" })}
                    </Button>
                </div>
            </div>

            <div className="mt-3 h-full min-h-0 w-full min-w-md-page flex gap-3">
                <div className="w-full flex flex-col gap-3">
                    <div className="min-h-0 flex gap-3">
                        <SceneList scenes={searchedScenes ?? []}
                                   selectedId={selectedScene?.id}
                                   modifiedScenes={modifiedScenes}
                                   invalidSceneIds={invalidSceneIds}
                                   welcomeSceneId={scenario?.properties?.welcome_scene_id}
                                   fallbackSceneId={scenario?.properties?.fallback_scene_id}
                                   onClickItem={id => setSelectedScene(getModifiedScene(searchedScenes?.find(scene => scene.id === id)))}
                                   onChangeWelcome={onChangeWelcome}
                                   onChangeFallback={onChangeFallback}
                                   onDuplicate={onDuplicate}
                                   onDelete={onClickDelete}
                                   isSubmitting={isSubmitting}
                        />

                        {selectedScene && (
                            <div key={selectedScene.id}
                                 ref={editorContainerRef}
                                 className="scene-edit-content-container scroll-gap-container floating-menu-clipping-ancestors"
                                 data-testid="scene-edit-content-container"
                            >
                                <SceneEditCard defaultValue={selectedScene}
                                               checkIsInvalidCustomId={customId => checkCustomIdUsed(customId, selectedScene.id)}
                                               onChange={(key, value) => debouncedChangeScene(selectedScene.id, key, value)}
                                />

                                <ParagraphEditor id={selectedScene.id}
                                                 className="ml-0.5"
                                                 defaultParagraphs={selectedScene.paragraphs}
                                                 onChange={paragraphs => debouncedChangeScene(selectedScene.id, "paragraphs", paragraphs)}
                                                 scrollToBottom={() => {
                                                    setTimeout(() => {
                                                        editorContainerRef.current?.scrollTo({
                                                            top: editorContainerRef.current?.scrollHeight,
                                                        });
                                                    }, 100);
                                                 }}
                                />
                            </div>
                        )}

                        {searchedScenes?.length === 0 && (
                            <div className="w-full h-no-item mr-3 flex items-center justify-center bg-white rounded-[6px] border border-black-200"
                                 data-testid="empty-notice"
                            >
                                <div className="text-[14px] font-[400] text-black-400">
                                    {searchKeyword
                                        ? intl.formatMessage({ id: "b0010" })
                                        : intl.formatMessage({ id: "i02027" })
                                    }
                                </div>
                            </div>
                        )}
                    </div>

                    {(!!searchedScenes?.length || selectedScene) && (
                        <div className="scroll-gap-container-align mt-auto flex justify-end items-center gap-2">
                            <Button size="xs"
                                    variant="danger"
                                    onClick={() => selectedScene && onClickDelete(selectedScene.id)}
                                    disabled={isSubmitting}
                            >
                                {intl.formatMessage({ id: "b0006" })}
                            </Button>

                            <Button size="xs"
                                    variant="primary"
                                    onClick={onClickSave}
                                    disabled={isSubmitting || Object.keys(modifiedScenes).length === 0 || invalidSceneIds.length > 0}
                            >
                                {intl.formatMessage({ id: "b0007" })}
                            </Button>
                        </div>
                    )}
                </div>

                {showWebchat && (
                    <div className="webchat-preview-container p-3 flex-none"
                         data-testid="webchat-preview-container">
                        <div className="webchat-preview-wrapper">
                            <WebChat scenarioId={scenarioId}
                                     theme={mergeThemeWithDefault(scenario?.publish?.webchat_theme)}
                                     locale={scenario?.properties?.language ?? "ko"}
                                     className="relative size-full"
                                     clientType="chat_only"
                                     footerType="cm"
                                     connectionConfig={{
                                         apiHost: `${API_HOST}`,
                                         socketHost: `${SOCKET_HOST}`,
                                         socketPath: `${WEBCHAT_SOCKET_PATH}`,
                                         method: "socket",
                                         test: true
                                     }}
                                     params={{
                                         always_new: "true"
                                     }}
                                     functionsRef={webchatFunctionsRef}
                                     disableAnimation
                                     onClickCloseButton={() => setShowWebchat(false)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </main>
    </>);
};


interface SceneEditCardProps {
    defaultValue?: Scene;
    checkIsInvalidCustomId: (customId: string, id?: number) => boolean;
    onChange: (key: keyof Scene, value: any) => void;
}

const SceneEditCard: React.FC<SceneEditCardProps> = (props) => {
    const { defaultValue: scene, checkIsInvalidCustomId, onChange } = props;

    const intl = useIntl();
    const { fullAutocomplete } = useScenarioAutocompletes();

    const [isCustomIdUsed, setIsCustomIdUsed] = useState<boolean>(false);

    if (!scene) {
        return null;
    }
    return (
        <div className="scene-card"
             data-testid="scene-card">
            <div className="field-label label-input-grid-label">
                {intl.formatMessage({ id: "i02014" })}
            </div>
            <div className="flex flex-col gap-1">
                <TextInput required
                           aria-label={intl.formatMessage({ id: "i02014" })}
                           defaultValue={scene.custom_id}
                           onChange={e => {
                               onChange("custom_id", e.target.value);
                               setIsCustomIdUsed(checkIsInvalidCustomId(e.target.value, scene.id));
                           }}
                />
                {isCustomIdUsed && (
                    <div className="font-[400] text-[12px] leading-[1.5] text-point-red">
                        {intl.formatMessage({ id: "i02024" })}
                    </div>
                )}
            </div>

            <div className="field-label">
                {intl.formatMessage({ id: "i02015" })}
            </div>
            <TextInput required
                       aria-label={intl.formatMessage({ id: "i02015" })}
                       defaultValue={scene.name}
                       onChange={e => onChange("name", e.target.value)}
            />

            <div className="flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i02016" })}
                </div>
                <Tooltip message={intl.formatMessage({ id: "i02017" })} />
            </div>
            <NumberInput min={0}
                         max={9999}
                         aria-label={intl.formatMessage({ id: "i02016" })}
                         defaultValue={scene.priority}
                         onChange={e => onChange("priority", parseInt(e.target.value) ?? 0)}
            />

            <div className="label-input-grid-label gap-x-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i02018" })}
                </div>
                <Tooltip message={intl.formatMessage({ id: "i02019" })} />
            </div>
            <Textarea rows={1}
                      aria-label={intl.formatMessage({ id: "i02018" })}
                      defaultValue={scene.entry_condition ?? ""}
                      onChange={value => onChange("entry_condition", value)}
                      autocompletes={fullAutocomplete}
            />
        </div>
    );
};

export default ScenarioEditView;
